import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';

import { Observable, catchError, map, of } from 'rxjs';

import { SentryLogger } from '@fcom/core';
import { isNull, stopPropagation } from '@fcom/core/utils';
import { Cabin, FinnairCabinClass } from '@fcom/dapi/api/models';
import { CmsCollection, CmsTemplate } from '@fcom/core-api';
import { ChipTheme, TabLayoutType } from '@fcom/ui-components';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { FragmentService } from '@fcom/common/services';

interface CabinClassContent {
  id: string;
  template: Observable<CmsTemplate>;
}
@Component({
  selector: 'fin-cabin-class-modal',
  styleUrls: ['./cabin-class-modal.component.scss'],
  templateUrl: './cabin-class-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CabinClassModalComponent implements OnInit {
  private gtmService = inject(GtmService);
  private fragmentService = inject(FragmentService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private sentryLogger = inject(SentryLogger);

  @Input()
  cabinClasses$: Observable<FinnairCabinClass[] | Cabin[]>;

  public readonly ChipTheme = ChipTheme;
  public readonly TabLayoutType = TabLayoutType;

  cabinClassModalOpen = false;
  selectedCabinClassId: string;
  availableCabinClasses$: Observable<CabinClassContent[]>;
  template$: Observable<CmsTemplate>;

  ngOnInit(): void {
    this.availableCabinClasses$ = this.cabinClasses$.pipe(
      map((cabinClasses: FinnairCabinClass[]) => {
        const cmsMap = {
          [FinnairCabinClass.ECONOMY]: { id: 'economy', url: 'fragments.cabinModalEconomy.url' },
          [FinnairCabinClass.ECOPREMIUM]: { id: 'ecopremium', url: 'fragments.cabinModalEcopremium.url' },
          [FinnairCabinClass.BUSINESS]: { id: 'business', url: 'fragments.cabinModalBusiness.url' },
        };

        return cabinClasses.map((cabinClass) => {
          return {
            id: cmsMap[cabinClass].id,
            template: this.fragmentService.getFragment<CmsCollection>(cmsMap[cabinClass].url).pipe(
              map((fragment) => ({ header: [], main: [fragment], footer: [] })),
              catchError((error: unknown) => {
                this.sentryLogger.error('Error loading cabin class modal fragment', { error });
                return of({ header: [], main: [], footer: [] });
              })
            ),
          };
        });
      })
    );
  }

  openModal(e: Event, id: string): void {
    stopPropagation(e);
    this.selectedCabinClassId = id;
    this.cabinClassModalOpen = true;
    this.gtmService.trackElement(
      'open-cabin-class-chip',
      GaContext.FLIGHT_SELECTION,
      ElementTypes.BUTTON,
      id,
      ElementActions.CLICK
    );

    if (isNull(e)) {
      this.changeDetectorRef.markForCheck();
    }
  }

  closeModal(): void {
    this.cabinClassModalOpen = false;
  }
}
